import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  Snackbar
} from '@mui/material';
import { Settings, RecordVoiceOver, Assessment, Speed, Save, Key, Group } from '@mui/icons-material';
import api from '../../../services/api';
import ProviderConfiguration from './ProviderConfiguration';
import VoiceTestingInterface from './VoiceTestingInterface';
import InterfaceConfiguration from './InterfaceConfiguration';
import MetricsDashboard from './MetricsDashboard';
import RoleVoiceConfiguration from './RoleVoiceConfiguration';
import ProviderAPIConfiguration from './ProviderAPIConfiguration';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tts-tabpanel-${index}`}
      aria-labelledby={`tts-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function TTSConfigurationManager({ token }) {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [configuration, setConfiguration] = useState(null);
  const [providers, setProviders] = useState([]);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ show: false, message: '', severity: 'success' });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfiguration();
    loadProviders();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tts/configuration', token);
      setConfiguration(response.data.configuration);
      setError(null);
    } catch (err) {
      console.error('Failed to load TTS configuration:', err);
      // If configuration doesn't exist yet, use default
      if (err.response?.status === 404 || err.response?.status === 401) {
        setConfiguration({
          providers: {
            elevenlabs: { enabled: true, priority: 1, costPer1000Chars: 0.18 },
            google: { enabled: false, priority: 2, costPer1000Chars: 0.016 },
            azure: { enabled: false, priority: 3, costPer1000Chars: 0.016 },
            openai: { enabled: false, priority: 4, costPer1000Chars: 0.015 }
          },
          interfaceConfigs: {
            client_chat: { enabled: true, provider: 'elevenlabs' },
            audio_export: { enabled: true, provider: 'google' },
            therapist_review: { enabled: true, provider: 'elevenlabs' },
            supervisor_chat: { enabled: true, provider: 'elevenlabs' },
            mobile_app: { enabled: true, provider: 'elevenlabs' }
          },
          platformRoleVoices: {}
        });
        setError(null); // Don't show error for initial setup
      } else {
        setError('Failed to load TTS configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProviders = async () => {
    try {
      const response = await api.get('/tts/providers', token);
      setProviders(response.data.providers || []);
    } catch (err) {
      console.error('Failed to load providers:', err);
      // Use default providers if API fails
      setProviders([
        {
          name: 'elevenlabs',
          displayName: 'ElevenLabs',
          available: true,
          costPer1000Chars: 0.18
        },
        {
          name: 'google',
          displayName: 'Google Cloud TTS',
          available: false,
          costPer1000Chars: 0.016
        },
        {
          name: 'azure',
          displayName: 'Azure Cognitive Services',
          available: false,
          costPer1000Chars: 0.016
        },
        {
          name: 'openai',
          displayName: 'OpenAI TTS',
          available: false,
          costPer1000Chars: 0.015
        }
      ]);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleConfigurationChange = (updatedConfig) => {
    setConfiguration(updatedConfig);
    setHasChanges(true);
  };

  const handleSaveConfiguration = async () => {
    try {
      const response = await api.put('/tts/configuration', configuration, token);
      
      if (response.data.success) {
        setSaveStatus({
          show: true,
          message: 'Configuration saved successfully',
          severity: 'success'
        });
        setHasChanges(false);
      }
    } catch (err) {
      console.error('Failed to save configuration:', err);
      setSaveStatus({
        show: true,
        message: 'Failed to save configuration',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSaveStatus({ ...saveStatus, show: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2">
            TTS Configuration Manager
          </Typography>
          {hasChanges && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={handleSaveConfiguration}
            >
              Save Changes
            </Button>
          )}
        </Box>

        <Typography variant="body2" color="textSecondary" paragraph>
          Configure text-to-speech providers, voice settings, and interface-specific configurations.
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="TTS configuration tabs" variant="scrollable" scrollButtons="auto">
            <Tab 
              icon={<Group />} 
              label="Voice Configuration" 
              id="tts-tab-0"
              aria-controls="tts-tabpanel-0"
            />
            <Tab 
              icon={<Key />} 
              label="API Settings" 
              id="tts-tab-1"
              aria-controls="tts-tabpanel-1"
            />
            <Tab 
              icon={<Settings />} 
              label="Providers" 
              id="tts-tab-2"
              aria-controls="tts-tabpanel-2"
            />
            <Tab 
              icon={<RecordVoiceOver />} 
              label="Voice Testing" 
              id="tts-tab-3"
              aria-controls="tts-tabpanel-3"
            />
            <Tab 
              icon={<Speed />} 
              label="Interface Config" 
              id="tts-tab-4"
              aria-controls="tts-tabpanel-4"
            />
            <Tab 
              icon={<Assessment />} 
              label="Metrics" 
              id="tts-tab-5"
              aria-controls="tts-tabpanel-5"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <RoleVoiceConfiguration
            token={token}
            configuration={configuration}
            onConfigChange={handleConfigurationChange}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <ProviderAPIConfiguration
            token={token}
            configuration={configuration}
            onConfigChange={handleConfigurationChange}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <ProviderConfiguration
            configuration={configuration}
            providers={providers}
            onConfigChange={handleConfigurationChange}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <VoiceTestingInterface
            token={token}
            configuration={configuration}
            providers={providers}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <InterfaceConfiguration
            configuration={configuration}
            providers={providers}
            onConfigChange={handleConfigurationChange}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <MetricsDashboard token={token} />
        </TabPanel>

        <Snackbar
          open={saveStatus.show}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={saveStatus.severity}>
            {saveStatus.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
}

export default TTSConfigurationManager;